import type { Component } from 'svelte';

import { hasNativeCalls } from '#lib/platform/calls.js';
import { supportsTouch } from '#lib/platform/os.js';
import { preferences } from '#lib/settings/preferences.svelte.js';
import { SETTINGS_ACCOUNT_SECTION } from '#lib/settings/registry.js';

import AppIconSettings from '#lib/features/settings/AppIconSettings.svelte';
import CallCompatibilityDebug from '#lib/features/settings/CallCompatibilityDebug.svelte';
import CallDeviceSettings from '#lib/features/call/CallDeviceSettings.svelte';
import ComposerButtons from '#lib/features/settings/ComposerButtons.svelte';
import ComposerEnterSetting from '#lib/features/settings/ComposerEnterSetting.svelte';
import CustomThemes from '#lib/features/settings/CustomThemes.svelte';
import FontSettings from '#lib/features/settings/FontSettings.svelte';
import QuickCss from '#lib/features/settings/QuickCss.svelte';
import DoubleTapReaction from '#lib/features/settings/DoubleTapReaction.svelte';
import MediaPreviewPrivacy from '#lib/features/settings/MediaPreviewPrivacy.svelte';
import MentionNotifications from '#lib/features/notifications/MentionNotifications.svelte';
import NotificationTroubleshooter from '#lib/features/notifications/NotificationTroubleshooter.svelte';
import NotificationDefaults from '#lib/features/notifications/NotificationDefaults.svelte';
import NotificationPermission from '#lib/features/notifications/NotificationPermission.svelte';
import NotificationKeywords from '#lib/features/settings/NotificationKeywords.svelte';
import PersonaSettings from '#lib/features/settings/PersonaSettings.svelte';
import PushGateway from '#lib/features/notifications/PushGateway.svelte';
import PushersSettings from '#lib/features/notifications/PushersSettings.svelte';
import SettingsFile from '#lib/features/settings/SettingsFile.svelte';
import SettingsSyncStatus from '#lib/features/settings/SettingsSyncStatus.svelte';
import SyncEncryption from '#lib/features/settings/SyncEncryption.svelte';
import StateEventTool from '#lib/features/settings/StateEventTool.svelte';
import DeveloperAccessToken from '#lib/features/settings/DeveloperAccessToken.svelte';
import DeveloperAccountData from '#lib/features/settings/DeveloperAccountData.svelte';
import DeveloperDebugLogs from '#lib/features/settings/DeveloperDebugLogs.svelte';
import DeveloperNotifications from '#lib/features/settings/DeveloperNotifications.svelte';
import DeveloperPushHistory from '#lib/features/settings/DeveloperPushHistory.svelte';
import DeveloperSearchMetrics from '#lib/features/settings/DeveloperSearchMetrics.svelte';
import DeveloperSentry from '#lib/features/settings/DeveloperSentry.svelte';
import DeveloperServerFeatures from '#lib/features/settings/DeveloperServerFeatures.svelte';
import DeveloperSyncDiagnostics from '#lib/features/settings/DeveloperSyncDiagnostics.svelte';
import SoundVolumeSettings from '../call/SoundVolumeSettings.svelte';

export interface CategoryPanel {
  component: Component;
  section?: string;
  start?: true;
  when?: () => boolean;
}

// Add panel anchors to settings/registry/panels.ts for search and link labels.
export const categoryPanels: Record<string, CategoryPanel[]> = {
  appearance: [
    { component: CustomThemes, section: 'themes' },
    { component: QuickCss, section: 'themes' },
    { component: AppIconSettings, section: 'themes' },
    { component: FontSettings, section: 'font' },
  ],
  composer: [
    { component: ComposerEnterSetting, section: 'writing', start: true },
    { component: ComposerButtons, section: 'composer-buttons' },
  ],
  timeline: [
    { component: DoubleTapReaction, section: 'messages', when: supportsTouch },
    {
      component: DeveloperSearchMetrics,
      section: 'developer-search-metrics',
      when: () => preferences.developerTools,
    },
  ],
  notifications: [
    { component: NotificationPermission, section: 'alerts', start: true },
    { component: NotificationDefaults },
    { component: MentionNotifications },
    { component: NotificationKeywords },
    { component: PushersSettings },
    { component: PushGateway },
    { component: NotificationTroubleshooter },
  ],
  calls: [
    {
      component: CallDeviceSettings,
      section: 'call-devices',
      when: () => !hasNativeCalls(),
    },
    { component: CallCompatibilityDebug, section: 'call-compat' },
  ],
  sounds: [
    {
      component: SoundVolumeSettings,
      section: 'volumes',
    },
  ],
  privacy: [{ component: MediaPreviewPrivacy, section: 'blurring', start: true }],
  personas: [{ component: PersonaSettings }],
  [SETTINGS_ACCOUNT_SECTION]: [
    { component: SettingsSyncStatus, section: 'sync', when: () => preferences.settingsSync },
    {
      component: SyncEncryption,
      section: 'sync',
      when: () => preferences.settingsSync && preferences.syncDrafts,
    },
    { component: SettingsFile, section: 'sync' },
  ],
  developer: [
    {
      component: DeveloperAccessToken,
      section: 'developer-options',
      when: () => preferences.developerTools,
    },
    {
      component: DeveloperSyncDiagnostics,
      section: 'developer-sync-diagnostics',
      when: () => preferences.developerTools,
    },
    {
      component: DeveloperServerFeatures,
      section: 'developer-server-features',
      when: () => preferences.developerTools,
    },
    {
      component: DeveloperAccountData,
      section: 'developer-account-data',
      when: () => preferences.developerTools,
    },
    {
      component: DeveloperNotifications,
      section: 'developer-notifications',
      when: () => preferences.developerTools,
    },
    {
      component: DeveloperPushHistory,
      section: 'developer-notifications',
      when: () => preferences.developerTools,
    },
    {
      component: DeveloperDebugLogs,
      section: 'developer-debug-logs',
      when: () => preferences.developerTools,
    },
    {
      component: DeveloperSentry,
      section: 'developer-sentry',
      when: () => preferences.developerTools,
    },
    {
      component: StateEventTool,
      section: 'settings-state-event',
      when: () => preferences.developerTools,
    },
  ],
};

export function panelsFor(categoryId: string): CategoryPanel[] {
  return categoryPanels[categoryId] ?? [];
}
